import React from 'react';
import { BarChart, Trash2, RefreshCw } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов ОСАГО
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты расчетов ОСАГО для сравнения.
        </p>
      </div>
    );
  }

  // Функция для получения названия типа автомобиля
  const getCarTypeName = (carType) => {
    const types = {
      passenger: 'Легковой',
      truck: 'Грузовой',
      bus: 'Автобус',
      motorcycle: 'Мотоцикл',
      tractor: 'Трактор',
      trailer: 'Прицеп'
    };
    return types[carType] || 'Транспортное средство';
  };
  
  // Форматирование даты
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {pluralize(savedCalculations.length, 'вариант', 'варианта', 'вариантов')}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<RefreshCw className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Дата расчета:</span>
                <span>{formatDate(calc.date)}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Тип ТС:</span>
                <span>{getCarTypeName(calc.formData.carType)}, {calc.formData.enginePower} л.с.</span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Регион:</span>
                <span>{calc.formData.regionCode}</span>
              </div>
              <div className="flex justify-between">
                <span>Стоимость ОСАГО:</span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(calc.results.totalPremium, 0)}
                </span>
              </div>
            </div>
            
            {/* Краткое сравнение с текущим расчетом */}
            {calc.results && calc.results.totalPremium && (
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between text-xs">
                  <span>Базовая премия:</span>
                  <span>{formatCurrency(calc.results.basePremium)}</span>
                </div>
                {calc.results.additionalPremium > 0 && (
                  <div className="flex justify-between text-xs">
                    <span>Доп. опции:</span>
                    <span>{formatCurrency(calc.results.additionalPremium)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {savedCalculations.length > 1 && (
        <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg">
          <h4 className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2">Сравнение вариантов</h4>
          <div className="space-y-1 text-xs text-emerald-600 dark:text-emerald-400">
            <div className="flex justify-between font-medium">
              <span>Самый выгодный:</span>
              <span>
                {formatCurrency(
                  Math.min(...savedCalculations.map(calc => calc.results.totalPremium))
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Самый дорогой:</span>
              <span>
                {formatCurrency(
                  Math.max(...savedCalculations.map(calc => calc.results.totalPremium))
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Средняя стоимость:</span>
              <span>
                {formatCurrency(
                  savedCalculations.reduce((sum, calc) => sum + calc.results.totalPremium, 0) / 
                  savedCalculations.length
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default ComparisonSidebar;